import React, { useState, useEffect, memo } from 'react';
import { API_BASE_URL } from '../../../utils/apiConfig';
import RouteMapModal from '../../RouteMapModal';

/**
 * TodayPrestation - Widget "Aujourd'hui" pour Chef d'équipe
 *
 * Affiche la prestation du jour où l'utilisateur est chef d'équipe.
 */
const TodayPrestation = memo(({ token, onNavigate }) => {
  const [prestation, setPrestation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    const fetchTodayPrestation = async () => {
      if (!token) return;

      setLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/dashboard/today-prestation`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (!response.ok) throw new Error('Erreur de chargement');

        const data = await response.json();
        setPrestation(data.success ? data.data : null);
      } catch (err) {
        console.error('Erreur fetch today prestation:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTodayPrestation();
  }, [token]);

  if (loading) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Aujourd'hui</h4>
        <div style={styles.loading}>Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Aujourd'hui</h4>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  if (!prestation) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Aujourd'hui</h4>
        <div style={styles.empty}>
          <span style={styles.emptyIcon}>📅</span>
          <p style={styles.emptyText}>Aucune prestation prévue aujourd'hui</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.title}>Aujourd'hui</h4>
        <button
          style={styles.viewButton}
          onClick={() => onNavigate && onNavigate('planning')}
        >
          Voir planning
        </button>
      </div>

      <div style={styles.prestationCard}>
        <div style={styles.cardHeader}>
          <span style={styles.badge}>P</span>
          <span style={styles.prestationNumber}>{prestation.prestation_number}</span>
          <span style={styles.clientName}>• {prestation.client_name || 'Client non défini'}</span>
        </div>

        <div style={styles.infoRow}>
          <span>📍</span>
          <span>{prestation.location_name || prestation.location_city || prestation.client_city || 'Lieu non défini'}</span>
          {prestation.distance_km && (
            <span style={styles.distanceBadge}>{prestation.distance_km} km</span>
          )}
          {prestation.departure_time && (
            <span style={styles.departureTime}>Départ {prestation.departure_time}</span>
          )}
        </div>

        {/* Bouton itinéraire */}
        {prestation.destination_lat && prestation.destination_lon && (
          <button
            style={styles.routeButton}
            onClick={() => setShowMap(true)}
          >
            🗺️ Voir l'itinéraire
          </button>
        )}

        <div style={styles.infoRow}>
          <span>👥</span>
          <span>{prestation.participant_count || 0} participant{(prestation.participant_count || 0) > 1 ? 's' : ''}</span>
          {prestation.target_participants && (
            <span style={styles.targetBadge}>
              / {prestation.target_participants} prévus
            </span>
          )}
        </div>

        {prestation.participants && prestation.participants.length > 0 && (
          <div style={styles.participantsList}>
            <div style={styles.participantsTitle}>Participants :</div>
            {prestation.participants.map((p, index) => (
              <div key={p.id || index} style={styles.participant}>
                <span style={styles.participantStatus}>
                  {p.on_site ? '✓' : '...'}
                </span>
                <span style={styles.participantName}>{p.name}</span>
                {p.on_site && <span style={styles.onSiteBadge}>sur place</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modale carte itinéraire */}
      <RouteMapModal
        isOpen={showMap}
        onClose={() => setShowMap(false)}
        destination={{
          lat: parseFloat(prestation.destination_lat),
          lon: parseFloat(prestation.destination_lon),
          name: prestation.location_name || prestation.client_name,
          address: prestation.location_address || prestation.client_address,
          city: prestation.location_city || prestation.client_city
        }}
        cachedGeometry={prestation.route_geometry}
        cachedAerialPhoto={prestation.aerial_photo}
        distanceKm={prestation.distance_km}
        token={token}
      />
    </div>
  );
});

TodayPrestation.displayName = 'TodayPrestation';

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a2e'
  },
  viewButton: {
    background: 'none',
    border: 'none',
    color: '#6366f1',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500'
  },
  loading: {
    textAlign: 'center',
    padding: '30px',
    color: '#6c757d'
  },
  error: {
    padding: '20px',
    backgroundColor: '#fff5f5',
    borderRadius: '8px',
    color: '#dc3545',
    textAlign: 'center'
  },
  empty: {
    textAlign: 'center',
    padding: '30px'
  },
  emptyIcon: {
    fontSize: '2rem',
    display: 'block',
    marginBottom: '8px'
  },
  emptyText: {
    margin: 0,
    color: '#6c757d'
  },
  prestationCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: '10px',
    padding: '16px',
    border: '1px solid #e9ecef'
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px'
  },
  badge: {
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    backgroundColor: '#28a745',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '0.9rem'
  },
  prestationNumber: {
    fontWeight: '600',
    fontSize: '1rem',
    color: '#1a1a2e'
  },
  clientName: {
    color: '#6c757d',
    fontSize: '0.95rem'
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    fontSize: '0.95rem'
  },
  departureTime: {
    marginLeft: 'auto',
    backgroundColor: '#e9ecef',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '0.85rem'
  },
  distanceBadge: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: '500'
  },
  routeButton: {
    width: '100%',
    padding: '10px 16px',
    marginTop: '12px',
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'background-color 0.2s'
  },
  targetBadge: {
    color: '#6c757d',
    fontSize: '0.9rem'
  },
  participantsList: {
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: '1px solid #dee2e6'
  },
  participantsTitle: {
    fontSize: '0.9rem',
    fontWeight: '500',
    marginBottom: '8px',
    color: '#495057'
  },
  participant: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 0',
    fontSize: '0.9rem'
  },
  participantName: {
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0
  },
  participantStatus: {
    width: '20px',
    color: '#28a745'
  },
  onSiteBadge: {
    marginLeft: 'auto',
    fontSize: '0.75rem',
    backgroundColor: '#d4edda',
    color: '#155724',
    padding: '2px 6px',
    borderRadius: '4px'
  }
};

export default TodayPrestation;
